import { createFileRoute } from '@tanstack/react-router'
import { useForm } from '@tanstack/react-form'
import { TextField } from '../../ui/text-field'
import { SelectField } from '../../ui/select-field'
import { useAccount } from 'wagmi'
import { useCreateBatch } from '../../api/create-batch'
import { AsyncButton } from '../../ui/AsyncButton'
import { BatchQrCode } from '../../ui/batch-qr-code'
import { useRole } from '../../context/role'

const AccessDenied = ({ roleLabel }: { roleLabel: string }) => (
  <div className="min-h-screen bg-warm-50 flex items-center justify-center">
    <div className="rounded-2xl border border-warm-200 bg-white p-8 shadow-sm text-center max-w-sm">
      <div className="text-3xl mb-3">🔒</div>
      <h2 className="text-base font-semibold text-warm-900 mb-1">Access restricted</h2>
      <p className="text-sm text-warm-500">
        The <span className="font-medium text-warm-700">{roleLabel}</span> role does not have permission to create batches.
      </p>
    </div>
  </div>
)

const BatchCreateForm = () => {
  const { address } = useAccount()
  const mutation = useCreateBatch()

  const form = useForm({
    defaultValues: {
      typeOfProduct:   '',
      cooperativeId:   '',
      dateOfCreateDpp: '',
    },
    onSubmit: async ({ value }) => {
      if (!address) {
        alert('Please connect your wallet')
        return
      }
      await mutation.mutateAsync(value)
    },
  })

  return (
    <div className="min-h-screen bg-warm-50 px-4 py-10">
      <div className="mx-auto max-w-xl">
        <div className="mb-6">
          <h1 className="font-display text-2xl font-semibold text-warm-900">Create Batch</h1>
          <p className="mt-1 text-sm text-warm-500">Register a new raw material batch</p>
        </div>

        {mutation.isPending && (
          <div className="mb-6 rounded-2xl border border-terra-200 bg-terra-50 p-6 animate-pulse">
            <div className="h-48 rounded-xl bg-terra-100" />
            <div className="mt-3 h-4 w-32 rounded bg-terra-100 mx-auto" />
          </div>
        )}

        {mutation.data?.batchUuid && (
          <div className="mb-6 rounded-2xl border border-terra-200 bg-terra-50 p-6">
            <BatchQrCode gtin={mutation.data.gtin} batchUuid={mutation.data.batchUuid} />
          </div>
        )}

        <div className="rounded-2xl border border-warm-200 bg-white p-6 shadow-sm">
          <form
            onSubmit={(e) => { e.preventDefault(); form.handleSubmit() }}
            className="flex flex-col gap-5"
          >
            <SelectField
              form={form}
              name="typeOfProduct"
              label="Product Type"
              options={[
                { value: 'cocoa',  label: 'Cocoa' },
                { value: 'cashew', label: 'Cashew' },
              ]}
            />

            <TextField form={form} name="cooperativeId"   label="Cooperative ID" />
            <TextField form={form} name="dateOfCreateDpp" label="Date" type="date" />

            {mutation.isError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {mutation.error instanceof Error ? mutation.error.message : 'Failed to create batch'}
              </div>
            )}

            <AsyncButton
              type="submit"
              variant="primary"
              pending={mutation.isPending || form.state.isSubmitting}
              className="mt-2 w-full"
            >
              {mutation.isPending ? 'Creating…' : 'Create Batch'}
            </AsyncButton>
          </form>
        </div>
      </div>
    </div>
  )
}

const BatchCreate = () => {
  const { config } = useRole()
  return config.canCreate ? <BatchCreateForm /> : <AccessDenied roleLabel={config.label} />
}

export const Route = createFileRoute('/_app/batch/create')({
  component: BatchCreate,
})
